/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { Box } from '@mui/material';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import RcTooltip from 'rc-tooltip';
import pluralize from '@src/utils/pluralize';
import {
  checkLowCredits,
  checkPackageExpired,
  hasPackageExpiringSoon,
} from '@src/services/package';
import IconInfoCircle from '@src/assets/icons/icon-info-circle.svg';
import {
  PAYMENT_HUB_RENEWAL_TYPE,
  SUBSCRIPTION_PURCHASE_STATUS,
} from '@src/constants/subscription';
import { isNumber } from 'lodash';
import apis from '@src/apis';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { COLOR } from '@src/styles/color';
import CustomTypography from '@src/new-components/Typography';
import {
  FREE_PACKAGE_CODES,
  PACKAGE_LEVEL,
  PACKAGE_STUDIO_VERSION,
} from '@src/constants/package';
import { delimitNumber } from '@src/utils/number';
import useUserInfo from '@src/hooks/useTtsUser';
import ROUTES from '@src/constants/route';
import i18n from '@src/languages';
import useTotalCredits from '@src/hooks/useTotalCredits';
import './index.css';
import NewDetailPackageInfo from '../NewDetailPackageInfo';

const FORMAT_DATETIME = 'DD/MM/YYYY - HH:mm';

const checkExpiredDate = (expiryDate) =>
  expiryDate ? moment().isAfter(expiryDate) : false;

const NewPackageInfoV3 = ({ useInfoCreditsApi }) => {
  const { getFeatureValue } = useFeatureFlags();
  const componentRef = useRef(null);
  const { t } = useTranslation();
  const { language } = i18n;
  const history = useHistory();
  const { subscriptionPurchase, usingPackage } = useSelector(
    (state) => state.user,
  );
  const { status } = subscriptionPurchase;

  const { user } = useSelector((state) => state.auth);

  const creditIntroConfig = getFeatureValue(FEATURE_KEYS.CREDITS_INTRO, {
    userId: user.id,
    email: user.email,
  });
  const creditIntroEnabled =
    creditIntroConfig !== undefined &&
    Object.hasOwn(creditIntroConfig, 'releaseDate');

  const {
    packageCode,
    packageExpiryDate,
    remainingCharacters = 0,
    bonusCharacters = 0,
    lockCharacters = 0,
  } = user;

  const [usingOrders, setUsingOrders] = useState([]);

  const { ttsUser } = useUserInfo();
  const { totalCredits, apiLimitRemaining } = useTotalCredits();
  const { studio } = ttsUser;
  const { cycle, topUp, custom } = studio || {};
  const {
    remainingCredits: remainingCycleCredits = 0,
    nextResetAt,
    purchasedTopUpCredits,
  } = cycle || {};
  const { remainingCredits: remainingTopUpCredits = 0 } = topUp || {};
  const { remainingCredits: remainingCustomCredits = 0 } = custom || {};

  const isFreePackage = FREE_PACKAGE_CODES.includes(packageCode);
  const isExpiredPackage = checkExpiredDate(packageExpiryDate);

  useEffect(() => {
    const fetchUsingOrders = async () => {
      const data = await apis.orders.getUsingOrders();
      if (data?.status) setUsingOrders(data.result);
    };
    fetchUsingOrders();
  }, []);

  const dateNotifyNearExpiry = getFeatureValue(
    FEATURE_KEYS.NOTIFY_NEAR_EXPIRY,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const usingStudioOrder = usingOrders?.find(
    (order) => order?.id === user?.orderId,
  );
  const autorenewEnabled = status === SUBSCRIPTION_PURCHASE_STATUS.ACTIVE;
  const isAutoRenewalPackage =
    usingStudioOrder?.renewalType === PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW &&
    autorenewEnabled;

  const getPackageExpired = () => {
    const isOldPackageVersion =
      packageCode?.includes(PACKAGE_STUDIO_VERSION.V1) ||
      packageCode?.includes(PACKAGE_STUDIO_VERSION.V2) ||
      packageCode?.includes(PACKAGE_STUDIO_VERSION.V2_1);

    if (isExpiredPackage && !isOldPackageVersion) {
      return `${t('exp')}: ${moment(packageExpiryDate).format(
        FORMAT_DATETIME,
      )} (${t('expired')})`;
    }
    if (
      isExpiredPackage &&
      !packageCode?.includes('V3') &&
      lockCharacters === 0
    ) {
      return t('outOfDate');
    }
    if (!packageExpiryDate && packageCode) {
      return `${t('exp')}: ${t('endless')}`;
    }
    if (packageExpiryDate) {
      return `${isAutoRenewalPackage ? t('autoRenewal') : t('exp')}: ${moment(
        packageExpiryDate,
      ).format(FORMAT_DATETIME)}`;
    }
    return '';
  };

  const totalCharacters =
    remainingCharacters +
    bonusCharacters +
    remainingCycleCredits +
    remainingTopUpCredits +
    remainingCustomCredits;

  const viewIconLockCycle = () => {
    const isLockedCycle =
      remainingCycleCredits &&
      !isFreePackage &&
      ttsUser?.studio?.cycle?.isLocked;
    const isLockedCustom =
      remainingCustomCredits && ttsUser?.studio?.custom?.isLocked;
    const isLockedTopUp =
      remainingTopUpCredits &&
      !isFreePackage &&
      ttsUser?.studio?.topUp?.isLocked;

    if (isLockedCycle || isLockedCustom || isLockedTopUp) {
      return (
        <LockRoundedIcon
          style={{
            color: COLOR.redPrimary,
            fontSize: 16,
            margin: '-2px 0px -2px 2px',
          }}
        />
      );
    }
    return null;
  };

  const formattedNextResetAt = moment(isFreePackage ? undefined : nextResetAt)
    .add(1, 'days')
    .format('DD/MM/YYYY');

  const isPackageExpiringSoon =
    isNumber(dateNotifyNearExpiry) &&
    !isFreePackage &&
    !!packageExpiryDate &&
    hasPackageExpiringSoon(packageExpiryDate, dateNotifyNearExpiry);

  const isLowCredits = checkLowCredits({
    totalCredits,
    packageCode,
  });
  const isPackageExpired = checkPackageExpired(packageExpiryDate);
  const isNotifyLowCredits =
    isLowCredits && !isPackageExpired && !useInfoCreditsApi;

  const headerTooltip = () => {
    if (useInfoCreditsApi && !isPackageExpiringSoon) {
      return (
        <CustomTypography
          variant="body-14-regular"
          color={COLOR.descriptionUpload}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            justifyContent: 'flex-end',
          }}
        >
          {t('remainingAPILimit')}
          <img
            style={{ width: 16, height: 16 }}
            src={IconInfoCircle}
            alt="icon-info-circle"
          />
        </CustomTypography>
      );
    }

    if (isPackageExpiringSoon) {
      return (
        <CustomTypography
          variant="body-14-regular"
          color="#FF692E"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            justifyContent: 'flex-end',
          }}
        >
          {t('packageExpiringSoon')}
          <WarningRoundedIcon sx={{ fontSize: '16px', color: '#FF692E' }} />
        </CustomTypography>
      );
    }

    if (isNotifyLowCredits) {
      return (
        <CustomTypography
          variant="body-14-regular"
          color="#FF692E"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            justifyContent: 'flex-end',
          }}
        >
          {t('lowCharacters')}
          <WarningRoundedIcon sx={{ fontSize: '16px', color: '#FF692E' }} />
        </CustomTypography>
      );
    }

    return (
      <CustomTypography
        variant="body-14-regular"
        color={COLOR.descriptionUpload}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          justifyContent: 'flex-end',
        }}
      >
        {t('remainingByCharacters')}
        <img
          style={{ width: 16, height: 16 }}
          src={IconInfoCircle}
          alt="icon-info-circle"
        />
      </CustomTypography>
    );
  };

  const userUseCustomPackage = useMemo(() => {
    const packageLevel = usingPackage?.level || '';
    return packageLevel === PACKAGE_LEVEL.CUSTOM;
  }, [usingPackage]);

  const getTotalCharacters = () => {
    let total = totalCharacters;
    if (useInfoCreditsApi && !userUseCustomPackage) {
      total = apiLimitRemaining;
    }
    if (useInfoCreditsApi && userUseCustomPackage) {
      total = remainingCustomCredits;
    }
    return `${delimitNumber(total, language)} ${pluralize(
      total,
      t('credit'),
      language,
    )}`;
  };

  return (
    <RcTooltip
      placement="bottomRight"
      overlay={
        <NewDetailPackageInfo
          formattedNextResetAt={formattedNextResetAt}
          purchasedTopUpCredits={purchasedTopUpCredits}
          getPackageExpired={getPackageExpired}
          isExpiredPackage={isExpiredPackage}
          remainingCycleCredits={remainingCycleCredits}
          remainingTopUpCredits={remainingTopUpCredits}
          remainingCustomCredits={remainingCustomCredits}
          remainingCharacters={remainingCharacters}
          lockCharacters={lockCharacters}
          bonusCharacters={bonusCharacters}
          isAutoRenewalPackage={isAutoRenewalPackage}
          isPackageExpiringSoon={isPackageExpiringSoon}
          isNotifyLowCredits={isNotifyLowCredits}
          apiLimitRemaining={apiLimitRemaining}
          useInfoCreditsApi={useInfoCreditsApi}
        />
      }
      trigger={['hover']}
      showArrow={false}
      align={{ offset: [0, 5] }}
    >
      <div
        ref={componentRef}
        className="wallet-package new-package-info-v3"
        style={{ marginLeft: creditIntroEnabled ? 0 : 24 }}
        onClick={() => history.push(ROUTES.SUBSCRIPTION)}
      >
        {headerTooltip()}
        {!isExpiredPackage ? (
          <CustomTypography
            variant="body-16-bold"
            color={COLOR.descriptionGray}
            style={{ textAlign: 'right' }}
          >
            {getTotalCharacters()}
            {viewIconLockCycle()}
          </CustomTypography>
        ) : (
          <Box className="characters-wrapper">
            <CustomTypography variant="body-16-bold" color={COLOR.textRejected}>
              {t('stillLockCharacters', {
                numberCharacters: delimitNumber(lockCharacters, language),
              })}
              {viewIconLockCycle()}
            </CustomTypography>
          </Box>
        )}
      </div>
    </RcTooltip>
  );
};

export default NewPackageInfoV3;
